/**
 * Production Configuration Contract — ARCHITECTURE.md §24, plus the media rules of §1,
 * the FPS SLA of §13 and the upload rules of §21 in machine-readable form.
 *
 * The policy is snapshotted into each session at creation time so that a mid-flight
 * config change can never make an in-progress session internally inconsistent.
 */
import { z } from 'zod';
export const displayedResolutionSchema = z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
export const mediaPolicySchema = z.object({
    displayAspectRatio: z.literal('9:16'),
    preferredQuality: z.literal('FHD'),
    fallbackQuality: z.literal('HD'),
    minimumQuality: z.literal('HD'),
    targetFps: z.literal(30),
    dynamicRange: z.literal('SDR'),
    allowGalleryImport: z.literal(false),
    allowPauseResume: z.literal(false),
    allowDigitalZoom: z.literal(false),
    /** §1 — displayed (rotation-applied) resolutions the backend accepts. */
    acceptedDisplayedResolutions: z.array(displayedResolutionSchema).min(1),
    /** Relative tolerance when comparing displayed aspect ratio to 9:16. */
    aspectRatioTolerance: z.number().min(0).max(0.05),
});
export const documentCaptureSchema = z.object({
    frontDurationMs: z.number().int().positive(),
    backDurationMs: z.number().int().positive(),
    minimumCardVisibilityRatio: z.number().min(0).max(1),
    minimumPassingFrameRatio: z.number().min(0).max(1),
    maximumContinuousBadIntervalMs: z.number().int().positive(),
    minimumConsecutiveReadyFrames: z.number().int().positive(),
    analysisFramesPerSecond: z.number().int().positive(),
});
export const focusPolicySchema = z.object({
    mode: z.literal('CONTINUOUS_AUTO_FOCUS'),
    target: z.literal('CARD_INNER_SURFACE'),
    focusRetryAfterFailedFrames: z.number().int().positive(),
    focusRetryCooldownMs: z.number().int().positive(),
    maximumTargetMovementRatio: z.number().positive(),
});
export const livenessPolicySchema = z.object({
    minimumStraightValidMs: z.number().int().positive(),
    minimumTotalDurationMs: z.number().int().positive(),
    targetTotalDurationMs: z.number().int().positive(),
    maximumNormalDurationMs: z.number().int().positive(),
    hardTimeoutMs: z.number().int().positive(),
    randomizeMovementSequence: z.literal(true),
    requireReturnToCenter: z.literal(true),
});
/** §13 — proposed FPS acceptance SLA, enforced on real presentation timestamps. */
export const fpsSlaSchema = z.object({
    requestedFps: z.literal(30),
    minAverageFps: z.number().positive(),
    maxAverageFps: z.number().positive(),
    maxDroppedFrameRatio: z.number().min(0).max(1),
    maxP95FrameGapMs: z.number().positive(),
    maxSingleFrameGapMs: z.number().positive(),
});
/** §21 — upload transport rules. */
export const uploadRulesSchema = z.object({
    chunkSizeBytes: z.number().int().positive(),
    maxFileBytesLicence: z.number().int().positive(),
    maxFileBytesSelfie: z.number().int().positive(),
    uploadTokenTtlSeconds: z.number().int().positive(),
    maxAttemptsPerStage: z.number().int().positive(),
});
/**
 * §8/§24 — provisional server-side quality thresholds. Production values MUST come from
 * signed remote config calibrated per platform/deviceClass/resolution/documentProfile;
 * these defaults exist so the pipeline is runnable before calibration and are marked so.
 */
export const qualityThresholdsSchema = z.object({
    source: z.enum(['SIGNED_REMOTE_CONFIG', 'PROVISIONAL_DEFAULTS']),
    profiledBy: z.array(z.string()),
    /** Normalized-ROI Laplacian variance minimum for the full card / face crop. */
    fullCardSharpnessMin: z.number().positive(),
    /** Tenengrad (Sobel energy) minimum on the same normalized crop. */
    tenengradMin: z.number().positive(),
    /** Fraction of pixels allowed in the near-black bucket (luma < 16). */
    maxUnderExposureRatio: z.number().min(0).max(1),
    /** Fraction of pixels allowed in the near-white bucket (luma > 250). */
    maxOverExposureRatio: z.number().min(0).max(1),
    /** Minimum grayscale standard deviation (contrast). */
    contrastMin: z.number().positive(),
    /** Maximum fraction of saturated specular pixels (glare) on the crop. */
    maxGlareRatio: z.number().min(0).max(1),
});
export const capturePolicySchema = z.object({
    mediaPolicy: mediaPolicySchema,
    documentCapture: documentCaptureSchema,
    focusPolicy: focusPolicySchema,
    livenessPolicy: livenessPolicySchema,
    fpsSla: fpsSlaSchema,
    uploadRules: uploadRulesSchema,
    qualityThresholds: qualityThresholdsSchema,
});
/** §24 values, verbatim where the contract specifies them. */
export const DEFAULT_CAPTURE_POLICY = {
    mediaPolicy: {
        displayAspectRatio: '9:16',
        preferredQuality: 'FHD',
        fallbackQuality: 'HD',
        minimumQuality: 'HD',
        targetFps: 30,
        dynamicRange: 'SDR',
        allowGalleryImport: false,
        allowPauseResume: false,
        allowDigitalZoom: false,
        acceptedDisplayedResolutions: [
            { width: 1080, height: 1920 },
            { width: 720, height: 1280 },
        ],
        aspectRatioTolerance: 0.01,
    },
    documentCapture: {
        frontDurationMs: 10_000,
        backDurationMs: 10_000,
        // Calibrated for hand-held capture (no tripod): tolerate natural movement while
        // still rejecting a card that is mostly out of frame or a sustained (~1s) blur.
        minimumCardVisibilityRatio: 0.75,
        minimumPassingFrameRatio: 0.7,
        maximumContinuousBadIntervalMs: 1_000,
        minimumConsecutiveReadyFrames: 3,
        analysisFramesPerSecond: 10,
    },
    focusPolicy: {
        mode: 'CONTINUOUS_AUTO_FOCUS',
        target: 'CARD_INNER_SURFACE',
        focusRetryAfterFailedFrames: 5,
        focusRetryCooldownMs: 1_500,
        maximumTargetMovementRatio: 0.04,
    },
    livenessPolicy: {
        minimumStraightValidMs: 8_000,
        minimumTotalDurationMs: 18_000,
        targetTotalDurationMs: 20_000,
        maximumNormalDurationMs: 24_000,
        hardTimeoutMs: 30_000,
        randomizeMovementSequence: true,
        requireReturnToCenter: true,
    },
    fpsSla: {
        requestedFps: 30,
        minAverageFps: 29.0,
        maxAverageFps: 31.0,
        maxDroppedFrameRatio: 0.02,
        maxP95FrameGapMs: 50,
        maxSingleFrameGapMs: 150,
    },
    uploadRules: {
        chunkSizeBytes: 5 * 1024 * 1024,
        maxFileBytesLicence: 150 * 1024 * 1024,
        maxFileBytesSelfie: 250 * 1024 * 1024,
        uploadTokenTtlSeconds: 600,
        maxAttemptsPerStage: 5,
    },
    qualityThresholds: {
        source: 'PROVISIONAL_DEFAULTS',
        profiledBy: ['platform', 'deviceClass', 'resolution', 'documentProfile', 'captureRegion'],
        // Handheld-calibrated at the server's 720px analysis width: accept "in focus and
        // readable", reject clearly blurry / badly exposed. Tighten per region in Phase 8.
        fullCardSharpnessMin: 20,
        tenengradMin: 150,
        maxUnderExposureRatio: 0.6,
        maxOverExposureRatio: 0.15,
        contrastMin: 10,
        maxGlareRatio: 0.08,
    },
};
//# sourceMappingURL=capture-policy.js.map